import Typography from 'components/Typography';
import styles from './index.module.scss';
import medicineStyles from '../index.module.scss';
import { useTheme } from 'hooks/theme';
import { useEffect, useState } from 'react';
import { medicineService } from 'services';
import { useIntl, useMemoTranslations } from 'hooks/Intl';
import * as translations from 'intls/medicine';
import LoadingSpinner from 'components/LoadingSpinner/LoadingSpinner';
import DocumentItem from './DocumentItem';
import useDebounce from 'hooks/useDebounce';
import { useFormik } from 'formik';
import Input from 'components/Input';

interface InformationProps {
  medicine: Medicine;
}

const Information = ({ medicine }: InformationProps) => {
  const { theme } = useTheme();
  const { idiomForApi } = useIntl();
  const intl = useMemoTranslations(translations);

  const [vmps, setVmps] = useState<VmpSummary[]>([]);
  const [fetchStatus, setFetchStatus] = useState<LoadingStatus>('NOT_LOADED');

  const fetchVmps = async (atcCode: string, query: string) => {
    try {
      setFetchStatus?.('LOADING');
      const { data: vmps } = await medicineService.getVmpsByAtcCode(
        atcCode,
        idiomForApi(),
        { q: query || undefined }
      );
      setVmps(vmps);
      setFetchStatus?.('LOADED');
    } catch (error) {
      console.log(error);
      setFetchStatus?.('ERROR');
    }
  };

  const formik = useFormik({
    initialValues: { query: '' },
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    onSubmit: () => {},
  });

  const fetchVmpsDebounced = useDebounce(
    () => fetchVmps(medicine.atc_code, formik.values.query),
    1000
  );

  useEffect(() => {
    fetchVmpsDebounced();
  }, [formik.values.query]);

  useEffect(() => {
    fetchVmps(medicine.atc_code, formik.values.query);
  }, [medicine.atc_code]);

  return (
    <div>
      <div
        className={`${medicineStyles.contentContainer} ${medicineStyles[theme]}`}
      >
        <Typography>{intl.FIND_DETAILED_INFORMATION}</Typography>
        <Input
          label={intl.FILTER_DOCUMENTS}
          formikProps={formik}
          name="query"
          maxLength={55}
        />
      </div>
      {fetchStatus === 'LOADING' && (
        <div>
          <LoadingSpinner fill />
        </div>
      )}
      {fetchStatus === 'LOADED' && vmps.length === 0 && (
        <Typography type="p" color="subtitle" className={styles.vmpsHeader}>
          {intl.MEDICINE_INFORMATION_NOT_FOUND}
        </Typography>
      )}
      {fetchStatus === 'LOADED' && vmps.length > 0 && (
        <>
          <Typography type="h6" className={styles.vmpsHeader} color="primary">
            {intl.DOCUMENTS}:
          </Typography>
          {vmps.map(vmp => (
            <DocumentItem key={vmp.external_id} vmp={vmp} />
          ))}
        </>
      )}
    </div>
  );
};

export default Information;
